import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Filet de sécurité global de l'application.
 *
 * Sans ce composant, la moindre erreur de rendu React (ex. une donnée reçue dans un
 * format inattendu) faisait remonter l'exception jusqu'à la racine et démontait tout
 * l'arbre : l'utilisateur se retrouvait devant une « page vierge » et devait rafraîchir.
 *
 * L'ErrorBoundary intercepte ces erreurs et affiche un écran de secours cliquable
 * (recharger / retour accueil) au lieu de laisser l'écran blanc.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Journalisation console (visible dans les rapports de bug joueurs).
    console.error('[ErrorBoundary] Erreur de rendu interceptée :', error, info?.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-zinc-950 p-6 text-center">
        <div className="max-w-md w-full rounded-2xl border border-zinc-800 bg-zinc-900 p-8 shadow-2xl">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 text-red-400">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="mb-2 text-lg font-bold text-white">Une erreur est survenue</h1>
          <p className="mb-6 text-sm text-zinc-400">
            L'affichage a rencontré un problème inattendu. Vous pouvez recharger la page
            sans perdre votre progression (sauvegardée sur le serveur).
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={this.handleReload}
              className="w-full rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-500"
            >
              Recharger la page
            </button>
            <a
              href="/"
              className="w-full rounded-xl border border-zinc-700 px-4 py-2.5 text-sm font-semibold text-zinc-300 transition-colors hover:bg-zinc-800"
            >
              Retour à l'accueil
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
